import { registerLocaleData } from "@angular/common";
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import localeFr from "@angular/common/locales/fr";
import { enableProdMode, importProvidersFrom, Injectable, LOCALE_ID } from "@angular/core";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";

import "hammerjs"; // HAMMER TIME
import { RouteReuseStrategy } from "@angular/router";
import { File } from "@awesome-cordova-plugins/file/ngx";
import { HTTP } from "@awesome-cordova-plugins/http/ngx";
import { LocationAccuracy } from "@awesome-cordova-plugins/location-accuracy/ngx";
import { IonicModule, IonicRouteStrategy, NavController } from "@ionic/angular";
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { DateTime } from "luxon";
import { AppRoutingModule } from "./app/app-routing.module";
import { AppComponent } from "./app/app.component";
import { APP_OPTIONS } from "./app/app.constants";
import { DaoModule } from "./core/dao/db/dao.module";
import { WsModule } from "./core/dao/ws/ws.module";
import { ServersConnectionsProvider } from "./core/providers/serversConnections.provider";
import { AppStateService } from "./core/services/appState.service";
import { AppAttachmentFactory, AttachmentService } from "./core/services/attachment.service";
import { DefaultDataDownloaderService } from "./core/services/defaultDataDownloader.service";
import { UserService } from "./core/services/user.service";
import { environment } from "./environments/environment";
import { KeyValueDbDao } from "./gyzmo-commons/dao/db/keyValue.db.dao";
import { AssetsHelper } from "./gyzmo-commons/helpers/assets.helper";
import { TestsHelper } from "./gyzmo-commons/helpers/tests.helper";
import { AngularHttpClient } from "./gyzmo-commons/http/angularHttpClient";
import { AngularHttpClientWithMock } from "./gyzmo-commons/http/angularHttpClientWithMock";
import { AttachmentFactory } from "./gyzmo-commons/interfaces/attachmentFactory";
import { DateProvider } from "./gyzmo-commons/interfaces/dateProvider";
import { IAppConfigurationProvider } from "./gyzmo-commons/interfaces/IAppConfigurationProvider";
import { IAppStateService } from "./gyzmo-commons/interfaces/IAppStateService";
import { IDefaultDataDownloaderService } from "./gyzmo-commons/interfaces/IDefaultDataDownloaderService";
import { IDocumentsProvider } from "./gyzmo-commons/interfaces/IDocumentsProvider";
import { IServerDefaultConnectionProvider } from "./gyzmo-commons/interfaces/IServerDefaultConnectionProvider";
import { IUserLoginService } from "./gyzmo-commons/interfaces/IUserLoginService";
import { MockDateProvider } from "./gyzmo-commons/providers/mockDateProvider";
import { RealDateProvider } from "./gyzmo-commons/providers/realDateProvider";
import { AppVersionService } from "./gyzmo-commons/services/appVersion.service";
import { DatabaseVersionService } from "./gyzmo-commons/services/databaseVersion.service";
import { LoggerService } from "./gyzmo-commons/services/logs/logger.service";

function createDateProvider() {
    return environment.mocked ? new MockDateProvider(DateTime.local(2023, 12, 11, 10, 10)) : new RealDateProvider();
}

registerLocaleData(localeFr);

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, "assets/i18n/", ".json");
}

export function createHttpClient(logger: LoggerService,
                                 httpClient: HttpClient,
                                 assetsHelper: AssetsHelper,
                                 keyValueDbDao: KeyValueDbDao) {
    return environment.mocked ? new AngularHttpClientWithMock(logger, httpClient, assetsHelper, keyValueDbDao) : new AngularHttpClient(logger, httpClient);
}

export class DefaultMissingTranslationHandler implements MissingTranslationHandler {
    handle(params: MissingTranslationHandlerParams) {
        return "~" + params.key;
    }
}

@Injectable({ providedIn: "root" })
class AppConfigurationProvider implements IAppConfigurationProvider {
    constructor(private navController: NavController) {
    }

    getAppName(): string {
        return "iMob Reception";
    }

    public getAppleAppId(): string {
        return "6740004731";
    }

    public navigateAfterConnection() {
        return this.navController.navigateRoot("/work-order-search");
    }

    public navigateToConnection() {
        return this.navController.navigateRoot("/connection", { state: { showForgottenPassword: false } });
    }
}

function getAppOptions() {
    let appOptions = APP_OPTIONS;

    const currentUrl = window.location.href;
    if (!appOptions["mode"] && currentUrl.endsWith("/#/ios")) {
        console.warn("App forced in ios mode !");
        appOptions.mode = "ios";
    }

    if (TestsHelper.isUnderE2eTest()) {
        appOptions.animated = false;
    }

    return appOptions;
}

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            BrowserModule,
            DaoModule,
            WsModule,
            AppRoutingModule,
            TranslateModule.forRoot({
                defaultLanguage: "en",
                missingTranslationHandler: { provide: MissingTranslationHandler, useClass: DefaultMissingTranslationHandler },
                loader: {
                    provide: TranslateLoader,
                    useFactory: (createTranslateLoader),
                    deps: [HttpClient],
                },
            }),
            IonicModule.forRoot(getAppOptions())),
        File,
        HTTP,
        LocationAccuracy,
        // Pour le test du processus de mise à jour de la structure base
        //{ provide: DatabaseVersionService, useValue: new DatabaseVersionServiceMock(1017) },
        //{ provide: AppVersionService, useValue: new AppVersionServiceMock("1.0.18", 1018) },
        // Pour la prod
        { provide: DatabaseVersionService, useClass: DatabaseVersionService },
        { provide: AppVersionService, useClass: AppVersionService },
        { provide: AngularHttpClient, useFactory: createHttpClient, deps: [LoggerService, HttpClient, AssetsHelper, KeyValueDbDao] },
        { provide: DateProvider, useFactory: createDateProvider },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: LOCALE_ID, useValue: "fr-FR" },
        { provide: IAppConfigurationProvider, useClass: AppConfigurationProvider },
        { provide: AttachmentFactory, useClass: AppAttachmentFactory },
        { provide: IDocumentsProvider, useExisting: AttachmentService },
        { provide: IUserLoginService, useExisting: UserService },
        { provide: IAppStateService, useExisting: AppStateService },
        { provide: IServerDefaultConnectionProvider, useExisting: ServersConnectionsProvider },
        { provide: IDefaultDataDownloaderService, useExisting: DefaultDataDownloaderService },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
    // eslint-disable-next-line no-console
    .catch(err => console.error(err));
